import { isEqual } from 'date-fns';
import { useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Checkbox } from '#src/atoms/inputs/Checkbox/index.tsx';
import { NativeSelect } from '#src/atoms/inputs/NativeSelect/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  HeadlineL,
  LabelM,
  LabelS,
} from '#src/atoms/typography/index.tsx';
import { IntlDate } from '#src/components/IntlDate/index.tsx';
import { EditableRow } from '#src/components/fiatWallet/CreateFiatWallet/EditableRow/index.tsx';
import {
  MANGOPAY_PRIVACY_POLICY,
  MANGOPAY_TERMS,
} from '#src/components/fiatWallet/CreateFiatWallet/externalLinks.ts';
import { useCurrencyOptions } from '#src/components/fiatWallet/CreateFiatWallet/useCurrencyOptions.ts';
import { useIsNotMajor } from '#src/components/fiatWallet/CreateFiatWallet/useIsNotMajor.ts';
import type { GraphQLResult } from '#src/components/form/Form/index.tsx';
import { GraphqlForm, TextField } from '#src/components/form/Form/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlCountries } from '#src/hooks/useIntlCountries.ts';
import { useCreateOrUpdateFiatWallet } from '#src/hooks/wallets/useCreateOrUpdateFiatWallet.ts';
import { glossary, userAttributes } from '#src/lib/glossary.ts';

const Content = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;

const Actions = styled(Vertical).attrs({ gap: 0 })`
  margin-top: var(--double-unit);
  flex-grow: 1;
  width: 100%;
  justify-content: flex-end;
`;

const StyledGraphqlForm = styled(GraphqlForm)`
  margin-bottom: 0;
  height: 100%;
`;

const Label = styled(BodyM)`
  color: var(--c-white);
`;

const CheckboxWrapper = styled.div`
  border-top: 1px solid var(--c-nd-200);
  padding-top: var(--double-unit);
`;

const StyledLink = styled.a`
  text-decoration: underline;
  color: var(--c-link);
`;

type Props = {
  onNext: () => void;
};

export const ReviewInfo = ({ onNext }: Props) => {
  const { update } = useCreateOrUpdateFiatWallet();
  const { fiatWalletAccountable } = useCurrentUserContext();
  const {
    firstName: initialFirstName,
    lastName: initialLastName,
    dob: initialDob,
    countryOfResidenceCode: initialCountryOfResidence,
    nationalityCode: initialNationality,
  } = fiatWalletAccountable || {};

  const countries = useIntlCountries(true);
  const currencies = useCurrencyOptions();

  const [year, month, day] = initialDob
    ? initialDob.split('-').map(Number)
    : [1990, 0, 1];

  const [firstName, setFirstName] = useState(initialFirstName || '');
  const [lastName, setLastName] = useState(initialLastName || '');
  const [dob, setDob] = useState<Date>(
    new Date(Date.UTC(year, month - 1, day))
  );
  const [countryOfResidenceCode, setCountryOfResidenceCode] = useState(
    initialCountryOfResidence || ''
  );
  const [nationalityCode, setNationalityCode] = useState(
    initialNationality || ''
  );
  const [currency, setCurrency] = useState(
    fiatWalletAccountable?.publicInfo?.currency || ''
  );
  const [termsAccepted, setTermsAccepted] = useState(false);

  const updateAndSubmit = async (onResult: (result: GraphQLResult) => void) => {
    const hasChanges =
      initialFirstName !== firstName ||
      initialLastName !== lastName ||
      (initialDob && !isEqual(dob, new Date(initialDob)));

    if (hasChanges) {
      const updateResult = await update({ firstName, lastName, dob });
      onResult(updateResult);
    }
    onNext();
  };

  const isNotMajor = useIsNotMajor(dob);

  const disabled =
    isNotMajor ||
    firstName === '' ||
    lastName === '' ||
    countryOfResidenceCode === '' ||
    nationalityCode === '' ||
    !currency ||
    !termsAccepted;

  return (
    <>
      <HeadlineL as="h3">
        <FormattedMessage defaultMessage="Review your information" />
      </HeadlineL>
      <BodyM>
        <FormattedMessage defaultMessage="Just a moment! Please review your information below for accuracy before continuing." />
      </BodyM>
      <StyledGraphqlForm
        onSubmit={(variables, onResult) => {
          updateAndSubmit(onResult);
        }}
        onSuccess={onNext}
        render={(Error, SubmitButton) => (
          <Content>
            <Vertical gap={0}>
              <EditableRow
                value={
                  countries.find(c => c.value === countryOfResidenceCode)
                    ?.label || ''
                }
                label={
                  <Label>
                    <FormattedMessage {...glossary.countryOfResidence} />
                  </Label>
                }
              >
                <NativeSelect
                  values={countries}
                  value={countryOfResidenceCode}
                  name="countryOfResidence"
                  fullWidth
                  onChange={setCountryOfResidenceCode}
                />
              </EditableRow>
              <EditableRow
                value={
                  countries.find(n => n.value === nationalityCode)?.label || ''
                }
                label={
                  <Label>
                    <FormattedMessage {...glossary.nationality} />
                  </Label>
                }
              >
                <NativeSelect
                  values={countries}
                  value={nationalityCode}
                  name="nationality"
                  fullWidth
                  onChange={setNationalityCode}
                />
              </EditableRow>
              <EditableRow
                value={firstName}
                label={
                  <Label>
                    <FormattedMessage {...glossary.firstName} />
                  </Label>
                }
              >
                <TextField
                  autoFocus
                  name="firstName"
                  defaultValue={firstName}
                  onChange={event => setFirstName(event.target.value)}
                />
              </EditableRow>
              <EditableRow
                value={lastName}
                label={
                  <Label>
                    <FormattedMessage {...glossary.lastName} />
                  </Label>
                }
              >
                <TextField
                  autoFocus
                  name="lastName"
                  defaultValue={lastName}
                  onChange={event => setLastName(event.target.value)}
                />
              </EditableRow>
              <EditableRow
                value={
                  <FormattedDate
                    value={dob}
                    day="numeric"
                    month="long"
                    year="numeric"
                    timeZone="UTC"
                  />
                }
                label={
                  <Label>
                    <FormattedMessage {...glossary.dateOfBirth} />
                  </Label>
                }
              >
                <IntlDate value={dob} onChange={setDob} />
              </EditableRow>
              {!!isNotMajor && (
                <LabelM color="var(--c-red-400)">
                  <FormattedMessage {...glossary.notMajorError} />
                </LabelM>
              )}
              <EditableRow
                value={currency}
                label={
                  <Label>
                    <FormattedMessage {...userAttributes.fiatCurrency} />
                  </Label>
                }
              >
                <NativeSelect
                  fullWidth
                  name="currency"
                  values={currencies}
                  value={currency}
                  onChange={setCurrency}
                />
              </EditableRow>
              <CheckboxWrapper>
                <Checkbox
                  name="mangopayTermsAndConditionsAccepted"
                  noPadding
                  checked={termsAccepted}
                  onChange={e => setTermsAccepted(e.target.checked)}
                  label={
                    <LabelS color="var(--c-white)">
                      <FormattedMessage
                        defaultMessage="I agree to Mangopay's <linkTerms>Terms and Conditions</linkTerms> and that my personal data will be processed pursuant to the <linkPrivacy>Privacy Policy</linkPrivacy>."
                        values={{
                          linkTerms: children => (
                            <StyledLink href={MANGOPAY_TERMS} target="_blank">
                              {children}
                            </StyledLink>
                          ),
                          linkPrivacy: children => (
                            <StyledLink
                              href={MANGOPAY_PRIVACY_POLICY}
                              target="_blank"
                            >
                              {children}
                            </StyledLink>
                          ),
                        }}
                      />
                    </LabelS>
                  }
                />
              </CheckboxWrapper>
            </Vertical>
            <Error />
            <Actions>
              <SubmitButton fullWidth size="medium" disabled={disabled}>
                <FormattedMessage {...glossary.next} />
              </SubmitButton>
            </Actions>
          </Content>
        )}
      />
    </>
  );
};
